<template>
  <TWModal
      :title="$t('users.editUser')"
      :loading="loading"
      :show="show"
      @close="handleClose"
      :allow-outside-click-to-close="false"
      :should-close-on-esc="searchOpen"
      ref="editUserModal"
  >
    <template v-slot:content>
      <form>
        <div>
          <form data-vv-scope="user-edit">
            <div class="mb-4">
              <label class="mb-1 inline-block" for="user-name">{{ $t('users.name') }}</label>
              <TextInput
                  id="user-name"
                  placeholder="e.g. Jo Bloggs"
                  v-model="user.name"
                  :has-error="errors.has('user-edit.name')"
                  data-ec="edit_user_modal"
                  data-et="user_name_input"
                  v-validate="'required'"
                  data-vv-name="name"
                  data-vv-as="name"
                  data-hj-suppress
              />
              <ValidationError v-show="errors.has('user-edit.name')">
                <template v-slot:text>
                  {{ errors.first('user-edit.name') }}
                </template>
              </ValidationError>
            </div>
            <div class="mb-4">
              <label class="mb-1 inline-block" for="user-email">{{ $t('users.email') }}</label>
              <EmailInput
                  v-model.trim="user.email"
                  :has-error="errors.has('user-edit.email')"
                  id="user-email"
                  placeholder="e.g. jo@example.com"
                  v-validate="'required|isTrimmedEmail'"
                  data-vv-name="email"
                  data-vv-as="email"
                  data-vv-scope="user-edit"
                  data-test="input-user-email"
                  data-hj-suppress
                  data-ec="edit_user_modal"
                  data-et="email_input"
              />
              <ValidationError v-show="errors.has('user-edit.email')">
                <template v-slot:text>
                  {{ errors.first('user-edit.email') }}
                </template>
              </ValidationError>
            </div>
            <div>
              <div class="mb-4">
                <label class="mb-1 float-left" for="user-role">{{ $t('users.role') }}</label>
                <p class="float-right mb-0 text-14"><a href="https://signable.help/users-teams-roles-explained/" target="_blank">{{ $t('users.learn-more-about-roles') }}</a></p>
                <ElSelect
                    id="user-role"
                    class="w-full"
                    v-model="user.role"
                >
                  <ElOption
                        v-for="item in userRoles"
                        :key="item"
                        :label="$t(`users.roles.${item}`)"
                        :value="item"
                        data-ec="edit_user_modal"
                        :data-et="`opt_role_selected_${item}`">
                  </ElOption>
                </ElSelect>
              </div>
              <div class="mb-2">
                <div class="flex justify-between flex-wrap">
                  <label class="mb-1 inline-block">{{ $t('users.add-them-to-a-team') }}</label>
                  <p class="float-right mb-0 text-14" v-if="!planHasFeature('team')">
                    <RouterLink :to="{ name: 'Billing'}" data-ec="upgrade-your-plan" data-et="upgrade-your-plan">
                      {{ $t('companyProfile.billing.upgradeForThisFeature') }}
                    </RouterLink>
                  </p>
                </div>
                <UserTeamSelector
                    v-model="user.groups"
                    :users-groups="user.groups"
                    :groups="groups"
                    data-ec="edit_user_modal"
                    :disabled="!planHasFeature('team')"
                    @select="handleTeamSelected"
                    @remove="handleTeamRemoved"
                ></UserTeamSelector>
              </div>
            </div>
          </form>
        </div>
      </form>
    </template>
    <template v-slot:controls>
      <div class="flex w-full justify-between">
        <button
            type="button"
            id="cancel-update-user-button"
            class="tw-button mr-2"
            @click="handleCancel"
            data-ec="edit_user_modal"
            data-et="btn_cancel">
          <XMarkIcon />
          <span>{{ $t('functional.cancel') }}</span>
        </button>
        <button
            type="button"
            id="update-user-button"
            class="tw-button tw-button-cta"
            @click="handleUpdateUser"
            data-ec="edit_user_modal"
            data-et="btn_save">
          <CheckIcon />
          <span>{{ $t('users.saveUser') }}</span>
        </button>
      </div>
    </template>
  </TWModal>
</template>

<script>
import TWModal from '@/components/tailwind/Modal.vue';
import TextInput from '@/components/tailwind/forms/TextInput.vue';
import EmailInput from '@/components/tailwind/forms/EmailInput.vue';
import { mapGetters } from 'vuex';
import ValidationError from '@/components/tailwind/forms/ValidationError.vue';
import UserTeamSelector from '@/components/tailwind/UserTeamSelector.vue';
import { logAmplitudeEvent } from '@/utils/analytics';
import { CheckIcon, XMarkIcon } from '@/components/icons/fontIcons';

export default {
  name: 'EditUserModal',
  inject: {
    $validator: '$validator',
  },
  props: {
    selectedUser: {
      type: Object,
      required: true,
    },
    show: {
      type: Boolean,
      default: false,
    },
  },

  components: {
    ValidationError,
    TWModal,
    TextInput,
    EmailInput,
    UserTeamSelector,
    CheckIcon,
    XMarkIcon,
  },
  data() {
    return {
      user: { ...this.selectedUser },
      loading: false,
      searchOpen: false,
    };
  },
  computed: {
    ...mapGetters({
      planHasFeature: 'plan/planHasFeature',
      groups: 'groups/pagedResults',
      userRoles: 'roles/userRoles',
    }),
  },
  watch: {
    show(newValue) {
      if (newValue) {
        this.$store.dispatch('groups/PAGINATE', { page: 1, pageLimit: 45 });
      }
    },
    selectedUser: {
      deep: true,
      handler(newVal) {
        this.user = { ...newVal };
      },
    },
  },
  methods: {
    handleTeamSelected() {
      logAmplitudeEvent('edit_user_modal--added_team_via_dropdown');
    },
    handleTeamRemoved() {
      logAmplitudeEvent('edit_user_modal--removed_team_via_dropdown');
    },
    handleUpdateUser() {
      this.$validator.validateAll('user-edit').then((validated) => {
        if (validated) {
          this.loading = true;
          this.$store.dispatch('users/UPDATE', this.user)
              .then(() => {
                this.$emit('user-updated');
              })
              .finally(() => {
                this.loading = false;
              });
          }
      });
    },
    handleClose($event) {
      if ($event && $event.target.classList.contains('multiselect__input')) {
        document.getElementById('update-user-button').focus();
        return;
      }

      // Reset the component to the initial state when created
      Object.assign(this.$data, this.$options.data.apply(this));
      this.$emit('close');
    },
    handleOpeningDeleteUserModal() {
        this.handleClose();
        this.$emit('delete-user');
    },
    handleOpeningResetPasswordModal() {
      this.handleClose();
      this.$emit('reset-user-password');
    },
    handleCancel() {
      this.handleClose();
      this.$emit('cancel');
    },
  },
};
</script>
